import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { AlertController, IonicModule, ModalController, NavParams, PopoverController } from '@ionic/angular';
import { NgxDatatableModule } from '@swimlane/ngx-datatable';
import Markdown from 'markdown-it';
import { NgxTippyModule } from 'ngx-tippy-wrapper';
import { firstValueFrom } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';

import {
  CUSTOM_FIELD_TYPE_MULTI_SELECT,
  CUSTOM_FIELD_TYPE_MULTI_USER_GROUP_SELECT,
  CUSTOM_FIELD_TYPE_MULTI_USER_SELECT,
  CUSTOM_FIELD_TYPE_SHORT_ANSWER,
  CUSTOM_FIELD_TYPE_SINGLE_SELECT,
  CUSTOM_FIELD_TYPE_USER_GROUP_SELECT,
  CUSTOM_FIELD_TYPE_USER_SELECT,
  STATUS_COLUMN_FORMAT_MAP,
} from 'src/app/constants';
import { generateCustomFieldsArray } from 'src/app/utils/utils';
import { FetchDataRequest, FetchDataResponse, createDataSource } from 'src/directives/paginated-datatable.directive';
import { SharedDirectivesModule } from 'src/directives/shared-directives.module';
import { environment } from 'src/environments/environment';
import { CustomField, SubStatus, Conversation, CustomFieldMetadata } from 'src/models';
import { SharedPipesModule } from 'src/pipes/shared-pipes.module';
import { AIAutofillService } from 'src/services/ai-autofill.service.js';
import { ApiService } from 'src/services/api.service';
import { CustomFieldsService } from 'src/services/custom-fields.service';
import { MessageService } from 'src/services/message.service';
import { SearchablePopoverService } from 'src/services/searchable-popover.service';

import { ActionDropdownComponent, ActionDropdownType } from '../action-dropdown/action-dropdown.component.js';
import { AvatarModule } from '../avatar/avatar.module';
import { ChannelSelectorModule } from '../channel-selector/channel-selector.module';
import { CustomFieldsModule } from '../custom-fields/custom-fields.module';
import { ResponseInputModule } from '../custom-fields/response-input/response-input.module';
import { TiptapEditorComponent } from '../tiptap-editor/tiptap-editor.component';

type SubStatusWithIcon = SubStatus & { icon?: string; iconClass?: string };

interface ConversationWithField {
  id: string;
  friendlyId: string;
  title: string;
  ticketFieldFeedback: boolean | null;
  ticketFieldValue: string;
  prefillAnalysis?: string;
  isSupportTeamMemberAnalysis?: boolean;
  createdAt: Date;
  updatedAt: Date;
}

@Component({
  standalone: true,
  imports: [
    CommonModule,
    IonicModule,
    FormsModule,
    ChannelSelectorModule,
    CustomFieldsModule,
    NgxTippyModule,
    AvatarModule,
    ResponseInputModule,
    NgxDatatableModule,
    SharedDirectivesModule,
    RouterModule,
    SharedPipesModule,
    TiptapEditorComponent,
  ],
  selector: 'app-edit-custom-field',
  templateUrl: './edit-custom-field.component.html',
  styleUrls: ['./edit-custom-field.component.scss'],
})
export class EditCustomFieldComponent implements OnInit {
  isDev: boolean;
  isLoading = false;
  id?: string;
  projectId: string;
  field: CustomField = {
    label: '',
    description: '',
    type: CUSTOM_FIELD_TYPE_SHORT_ANSWER,
    required: false,
    public: true,
    id: uuidv4(),
    conditions: {
      type: 'simple',
      simple: {},
    },
    prefillOnSubStatusId: null,
    prefillPrompt: null,
    requiredOnSubStatusId: null,
  };

  // Analysis editing states
  editingRowId: string | null = null;
  editingAnalysis: string = '';
  isSavingAnalysis = false;

  availableSubStatuses: SubStatusWithIcon[] = [];
  selectedSubStatusDisplay: SubStatusWithIcon | null = null;
  selectedRequiredSubStatusDisplay: SubStatusWithIcon | null = null;

  conversationsWithField: Partial<Conversation>[] = [];
  conversationsDataSource;
  isConversationsLoading = false;

  constructor(
    navParams: NavParams,
    private modalCtrl: ModalController,
    private alertCtrl: AlertController,
    private msgSrvc: MessageService,
    private apiService: ApiService,
    private customFieldsService: CustomFieldsService,
    private searchablePopoverService: SearchablePopoverService,
    private popoverCtrl: PopoverController,
    private aiAutofillService: AIAutofillService,
  ) {
    this.id = navParams.get('id');
    this.projectId = navParams.get('projectId');
    this.isDev = environment.environment === 'dev';
  }

  async ngOnInit() {
    await this.loadSubStatuses();
    if (this.id) {
      await this.loadCustomField();
      await this.initializeConversationsTable();
    }
  }

  async initializeConversationsTable() {
    if (!this.id) return;
    this.conversationsDataSource = createDataSource(this.fetchConversationsWithField);
    await this.loadConversations();
  }

  // Helper function to find metadata for a specific custom field
  private getFieldMetadata(conversation: Conversation, fieldId: string): CustomFieldMetadata | null {
    if (!conversation.ticketFieldMetadata || !Array.isArray(conversation.ticketFieldMetadata)) {
      return null;
    }
    return conversation.ticketFieldMetadata.find((metadata) => metadata.customFieldId === fieldId) || null;
  }

  fetchConversationsWithField = async ({
    limit = 10,
    cursor,
    order = ['createdAt'],
    descending = true,
  }: FetchDataRequest): Promise<FetchDataResponse<Partial<Conversation>>> => {
    if (!this.id) {
      return { data: [], cursors: { next: undefined }, totalCount: 0 };
    }
    this.isConversationsLoading = true;
    try {
      const response = (await this.customFieldsService.getConversationsWithField(this.id, {
        limit,
        cursor,
        order,
        descending,
      })) as {
        data: Conversation[];
        cursors: { next?: string; previous?: string };
        totalCount: number;
      };

      const currentPageData: ConversationWithField[] = (response.data || []).map((conv: Conversation) => {
        const fieldMetadata = this.getFieldMetadata(conv, this.id);
        return {
          id: conv.id,
          friendlyId: conv.friendlyId,
          title: conv.title,
          ticketFieldFeedback: fieldMetadata?.approvedPrefill,
          ticketFieldValue: fieldMetadata?.suggestedValue,
          prefillAnalysis: fieldMetadata?.prefillAnalysis,
          isSupportTeamMemberAnalysis: fieldMetadata?.isSupportTeamMemberAnalysis,
          createdAt: conv.createdAt,
          updatedAt: conv.updatedAt,
        };
      });

      return {
        data: currentPageData,
        cursors: response.cursors,
        totalCount: response.totalCount,
      };
    } catch (error) {
      console.error('Error fetching conversations with field:', error);
      this.msgSrvc.showError('Failed to load conversations using this field.');
      if (!cursor) {
        this.conversationsWithField = [];
      }
      return { data: [], cursors: { next: undefined }, totalCount: 0 };
    } finally {
      this.isConversationsLoading = false;
    }
  };

  async loadConversations() {
    if (this.conversationsDataSource) {
      await this.conversationsDataSource.firstPage();
    }
  }

  async loadSubStatuses() {
    if (!this.projectId) {
      console.warn('Project ID not available, cannot load sub-statuses.');
      return;
    }
    try {
      const result = await firstValueFrom(
        this.apiService.post<{ data: SubStatus[] }>('/sub-statuses/list', {
          where: [{ field: 'projectId', operator: '==', value: this.projectId }],
          order: [
            ['typeOrd', 'ASC'],
            ['position', 'ASC'],
          ],
          limit: 1000,
        }),
      );
      this.availableSubStatuses = (result.data || []).map((subStatus) => {
        const type = subStatus.key.split('__')[0];
        const format = STATUS_COLUMN_FORMAT_MAP[type];
        return {
          ...subStatus,
          icon: format?.icon,
          iconClass: format?.iconClass,
        };
      });
    } catch (error) {
      console.error('Error loading sub-statuses:', error);
      this.msgSrvc.showError('Failed to load status options.');
      this.availableSubStatuses = [];
    }
    this.updateSelectedSubStatusDisplay();
  }

  async loadCustomField() {
    try {
      const customField = await this.customFieldsService.getById(this.id);
      this.field = customField;
      this.updateSelectedSubStatusDisplay();
    } catch (error) {
      await this.msgSrvc.showError(error);
    }
  }

  updateSelectedSubStatusDisplay() {
    if (this.field.prefillOnSubStatusId) {
      this.selectedSubStatusDisplay =
        this.availableSubStatuses.find((s) => s.id === this.field.prefillOnSubStatusId) || null;
    } else {
      this.selectedSubStatusDisplay = null;
    }

    if (this.field.requiredOnSubStatusId) {
      this.selectedRequiredSubStatusDisplay =
        this.availableSubStatuses.find((s) => s.id === this.field.requiredOnSubStatusId) || null;
    } else {
      this.selectedRequiredSubStatusDisplay = null;
    }
  }

  showSubStatusPopover(event: Event) {
    const popoverItems = [
      { id: null, title: 'None', icon: undefined, iconClass: undefined, object: null },
      ...this.availableSubStatuses.map((subStatus) => ({
        id: subStatus.id,
        title: `${subStatus.name} (${subStatus.type})`,
        icon: subStatus.icon,
        iconClass: subStatus.iconClass,
        object: subStatus,
      })),
    ];

    this.searchablePopoverService.showPopover({
      event,
      selectedItemId: this.field.prefillOnSubStatusId ?? undefined,
      showSearch: false,
      componentView: 'SubStatusSelect',
      initialItems: popoverItems,
      callback: (id, _name, object) => {
        this.field.prefillOnSubStatusId = id;
        this.selectedSubStatusDisplay = object;
      },
      loadData: async () => {
        return popoverItems;
      },
    });
  }

  showRequiredSubStatusPopover(event: Event) {
    // Filter out intake (draft) and the first open status since fields can't be required at initial stages
    const validSubStatuses = this.availableSubStatuses.filter((subStatus) => subStatus.type !== 'draft');

    const popoverItems = [
      { id: null, title: 'None', icon: undefined, iconClass: undefined, object: null },
      ...validSubStatuses.map((subStatus) => ({
        id: subStatus.id,
        title: `${subStatus.name} (${subStatus.type})`,
        icon: subStatus.icon,
        iconClass: subStatus.iconClass,
        object: subStatus,
      })),
    ];

    this.searchablePopoverService.showPopover({
      event,
      selectedItemId: this.field.requiredOnSubStatusId ?? undefined,
      showSearch: false,
      componentView: 'SubStatusSelect',
      initialItems: popoverItems,
      callback: (id, _name, object) => {
        this.field.requiredOnSubStatusId = id;
        this.selectedRequiredSubStatusDisplay = object;
      },
      loadData: async () => {
        return popoverItems;
      },
    });
  }

  dismiss(customField?: CustomField) {
    this.modalCtrl.dismiss({ customField });
  }

  async submit() {
    const canSetAllowedValues = [
      CUSTOM_FIELD_TYPE_USER_SELECT,
      CUSTOM_FIELD_TYPE_USER_GROUP_SELECT,
      CUSTOM_FIELD_TYPE_MULTI_USER_SELECT,
      CUSTOM_FIELD_TYPE_MULTI_USER_GROUP_SELECT,
    ].includes(this.field.type);

    const canSetOptions = [CUSTOM_FIELD_TYPE_SINGLE_SELECT, CUSTOM_FIELD_TYPE_MULTI_SELECT].includes(this.field.type);

    const cleanedField = {
      ...this.field,
      label: this.field.label?.trim() || `New ${this.field.type} field`,
      options: canSetOptions && !this.field.externalSelect ? this.field.options : null,
      allowedValues: canSetAllowedValues ? this.field.allowedValues : null,
      projectId: this.projectId,
      prefillOnSubStatusId: this.field.prefillOnSubStatusId,
      prefillPrompt: this.field.prefillPrompt,
      requiredOnSubStatusId: this.field.requiredOnSubStatusId,
    };
    try {
      if (this.id) {
        const result = await this.customFieldsService.updatePartial(this.id, cleanedField);
        await this.msgSrvc.show('Custom field updated!');
        this.dismiss(result);
      } else {
        const result = await this.customFieldsService.create(cleanedField);
        await this.msgSrvc.show('Custom field added!');
        this.dismiss(result);
      }
    } catch (error) {
      await this.msgSrvc.showError(error);
    } finally {
      this.isLoading = false;
    }
  }

  async delete() {
    const alert = await this.alertCtrl.create({
      header: 'Delete custom field?',
      subHeader: `Please remove this field from any ticket types before deleting. Deletion can't be undone.`,
      buttons: [
        {
          text: 'Nevermind',
        },
        {
          text: 'Delete',
          handler: async () => {
            try {
              this.isLoading = true;
              await this.customFieldsService.delete(this.id);
              this.isLoading = false;
              this.dismiss();
            } catch (e) {
              this.isLoading = false;
              this.msgSrvc.showError(e);
            }
          },
        },
      ],
    });
    alert.present();
  }

  async duplicate() {
    const alert = await this.alertCtrl.create({
      header: 'Duplicate custom field?',
      subHeader: `You can confirm details before you save.`,
      buttons: [
        {
          text: 'Cancel',
        },
        {
          text: 'Duplicate',
          handler: () => {
            this.field.id = undefined;
            this.id = undefined;
            setTimeout(() => {
              this.field.label = `Copy of ${this.field.label}`;
            }, 500);
          },
        },
      ],
    });
    alert.present();
  }

  getPreviewField() {
    return generateCustomFieldsArray([this.field], [], {});
  }

  private async approveAIPrefill(row: ConversationWithField): Promise<void> {
    if (!row.id) {
      console.error('Missing conversationId for approving prefill.');
      return;
    }
    try {
      await this.aiAutofillService.approvePrefill({ conversationId: row.id, fieldId: this.id });
      await this.conversationsDataSource.reloadPage();
    } catch (error) {
      console.error('Error approving prefill for field:', this.id, error);
    }
  }

  private async rejectAIPrefill(row: ConversationWithField): Promise<void> {
    if (!row.id) {
      console.error('Missing conversationId for rejecting prefill.');
      return;
    }
    try {
      await this.aiAutofillService.rejectPrefill({ conversationId: row.id, fieldId: this.id });
      await this.conversationsDataSource.reloadPage();
    } catch (error) {
      console.error('Error rejecting prefill for field:', this.id, error);
    }
  }

  async showPrefillOption(row: ConversationWithField) {
    const LABEL_APPROVE_PREFILL = 'Approve Prefill';
    const LABEL_REJECT_PREFILL = 'Reject Prefill';

    const options: ActionDropdownType[] = [
      {
        label: LABEL_APPROVE_PREFILL,
        icon: 'thumbs-up-outline',
      },
      {
        label: LABEL_REJECT_PREFILL,
        icon: 'trash-outline',
        destructive: true,
      },
    ];

    const popover = await this.popoverCtrl.create({
      component: ActionDropdownComponent,
      event,
      showBackdrop: false,
      componentProps: {
        options,
        callback: async (_idx: number, label: string) => {
          popover.dismiss();

          if (label === LABEL_APPROVE_PREFILL) {
            await this.approveAIPrefill(row);
          } else if (label === LABEL_REJECT_PREFILL) {
            await this.rejectAIPrefill(row);
          }
        },
      },
    });
    popover.present();
  }

  renderMarkdown(content: string): string {
    if (!content) return '';

    const md = new Markdown({
      breaks: true,
      linkify: true,
      html: true,
    });

    return md.render(content);
  }

  startEditingAnalysis(row: ConversationWithField): void {
    this.editingRowId = row.id;
    this.editingAnalysis = row.prefillAnalysis || '';
  }

  cancelEditingAnalysis(): void {
    this.editingRowId = null;
    this.editingAnalysis = '';
  }

  async saveAnalysis(row: ConversationWithField): Promise<void> {
    if (!this.id || !row.id) {
      console.error('Missing required IDs for saving analysis');
      return;
    }

    try {
      this.isSavingAnalysis = true;
      await this.aiAutofillService.updatePrefillAnalysis({
        conversationId: row.id,
        fieldId: this.id,
        analysis: this.editingAnalysis,
      });

      // Reset editing state
      this.editingRowId = null;
      this.editingAnalysis = '';

      // Reload the data to show updated analysis
      await this.conversationsDataSource.reloadPage();
      this.msgSrvc.show('Analysis updated successfully');
    } catch (error) {
      console.error('Error saving analysis:', error);
      this.msgSrvc.showError('Failed to save analysis');
    } finally {
      this.isSavingAnalysis = false;
    }
  }
}
